import type {
  QueueCode,
  ServiceCategory,
  WorkbenchQuery,
} from "../api/types.js";
import type { WorkbenchGrouping } from "../features/workbench/WorkbenchTable.js";

/** Lenses the workbench exposes. They map to canonical server queue codes. */
export type WorkbenchLens =
  | "NOW"
  | "COMING"
  | "INVESTIGATE"
  | "DISQUALIFIED_SERVICE_NEED";

/**
 * Working-set state lives in the URL so ordinary work never destroys it.
 * Filters, lens, grouping, and cursor are shareable and survive navigation to a
 * Lab and back; only transient UI state such as the drawer and scroll position
 * stays in component state.
 */
export interface WorkbenchState {
  readonly lens: WorkbenchLens;
  readonly grouping: WorkbenchGrouping;
  readonly organizationId?: string | undefined;
  readonly locationId?: string | undefined;
  readonly parentLocationId?: string | undefined;
  readonly serviceCategory?: ServiceCategory | undefined;
  readonly provider?: string | undefined;
  readonly contactPresence?: "known" | "unknown" | undefined;
  /** A building is worked together with the rooms directly inside it. */
  readonly includeChildLocations?: boolean | undefined;
  readonly cursor?: string | undefined;
  readonly selectedProfileId?: string | undefined;
}

export const DEFAULT_WORKBENCH_STATE: WorkbenchState = {
  lens: "NOW",
  grouping: "attention",
};

const LENSES: readonly WorkbenchLens[] = [
  "NOW",
  "COMING",
  "INVESTIGATE",
  "DISQUALIFIED_SERVICE_NEED",
];

export function lensLabel(lens: WorkbenchLens): string {
  if (lens === "NOW") return "Needs attention";
  if (lens === "COMING") return "Upcoming";
  if (lens === "INVESTIGATE") return "Explore";
  return "Review service fit";
}

/** Parse the workbench portion of a search string. Unknown values are ignored. */
export function parseWorkbenchState(search: string): WorkbenchState {
  const params = new URLSearchParams(search);
  const lens = params.get("lens");
  const grouping = params.get("group");
  const category = params.get("category");
  const contact = params.get("contact");
  return {
    lens: LENSES.includes(lens as WorkbenchLens)
      ? (lens as WorkbenchLens)
      : DEFAULT_WORKBENCH_STATE.lens,
    grouping: grouping === "place" ? "place" : "attention",
    ...optional("organizationId", params.get("org")),
    ...optional("locationId", params.get("loc")),
    ...optional("parentLocationId", params.get("parent")),
    ...(category === "pipette_calibration" || category === "balance_calibration"
      ? { serviceCategory: category }
      : {}),
    ...optional("provider", params.get("provider")),
    ...(contact === "known" || contact === "unknown"
      ? { contactPresence: contact }
      : {}),
    ...(params.get("rooms") === "1" ? { includeChildLocations: true } : {}),
    ...optional("cursor", params.get("cursor")),
    ...optional("selectedProfileId", params.get("row")),
  };
}

function optional<K extends keyof WorkbenchState>(
  key: K,
  value: string | null,
): Pick<WorkbenchState, K> | Record<string, never> {
  return value ? ({ [key]: value } as Pick<WorkbenchState, K>) : {};
}

/**
 * Serialize working-set state back to a query string. Empty filters and
 * defaults are omitted so a plain working set keeps a clean URL.
 */
export function serializeWorkbenchState(state: WorkbenchState): string {
  const params = new URLSearchParams();
  if (state.lens !== DEFAULT_WORKBENCH_STATE.lens) {
    params.set("lens", state.lens);
  }
  if (state.grouping !== DEFAULT_WORKBENCH_STATE.grouping) {
    params.set("group", state.grouping);
  }
  if (state.organizationId) params.set("org", state.organizationId);
  if (state.locationId) params.set("loc", state.locationId);
  if (state.parentLocationId) params.set("parent", state.parentLocationId);
  if (state.serviceCategory) params.set("category", state.serviceCategory);
  if (state.provider) params.set("provider", state.provider);
  if (state.contactPresence) params.set("contact", state.contactPresence);
  if (state.includeChildLocations) params.set("rooms", "1");
  if (state.cursor) params.set("cursor", state.cursor);
  if (state.selectedProfileId) params.set("row", state.selectedProfileId);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** Translate working-set state into the bounded server read. */
export function toWorkbenchQuery(
  state: WorkbenchState,
  limit = 50,
): WorkbenchQuery {
  return {
    queueCode: state.lens as QueueCode,
    ...(state.organizationId ? { organizationId: state.organizationId } : {}),
    ...(state.locationId ? { locationId: state.locationId } : {}),
    ...(state.parentLocationId
      ? { parentLocationId: state.parentLocationId }
      : {}),
    ...(state.serviceCategory
      ? { serviceCategory: state.serviceCategory }
      : {}),
    ...(state.provider ? { provider: state.provider } : {}),
    ...(state.contactPresence
      ? { contactPresence: state.contactPresence }
      : {}),
    ...(state.includeChildLocations ? { includeChildLocations: true } : {}),
    ...(state.cursor ? { cursor: state.cursor } : {}),
    limit,
  };
}

export function hasActiveFilters(state: WorkbenchState): boolean {
  return Boolean(
    state.organizationId ||
      state.locationId ||
      state.parentLocationId ||
      state.serviceCategory ||
      state.provider ||
      state.contactPresence,
  );
}

/**
 * One text field narrows the bounded set the user is already looking at. It is
 * deliberately local: global Search stays the way to reach territory that is
 * not in this working set.
 */
export function matchesLocalSearch(
  item: {
    lab: { name: string; roomOrSuite?: string | null };
    location: { name: string };
    organization: { name: string };
    contact: { name: string } | null;
    projection: { currentProviderName: string | null };
  },
  query: string,
): boolean {
  const wanted = query.trim().toLocaleLowerCase();
  if (!wanted) return true;
  return [
    item.lab.name,
    item.lab.roomOrSuite ?? "",
    item.location.name,
    item.organization.name,
    item.contact?.name ?? "",
    item.projection.currentProviderName ?? "",
  ]
    .join(" ")
    .toLocaleLowerCase()
    .includes(wanted);
}
